import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, Shield, KeyRound } from "lucide-react";

const MODULE_LABELS = {
  dashboard: "Pano",
  tractors: "Traktörler",
  service: "Servis",
  parts: "Yedek Parça",
  customers: "Müşteriler",
  quotations: "Teklifler",
  finance: "Finans",
  reports: "Raporlar",
  users: "Kullanıcı Yönetimi",
  part_requests: "Parça Talepleri",
  part_orders: "Parça Siparişleri",
  service_requests: "Servis Talepleri",
  notes: "Senet & Çek",
  settings: "Ayarlar",
  expenses_create: "Gider Girişi",
  expenses_view: "Giderleri Görme / Yönetme",
};

const ROLE_LABELS = { admin: "Yönetici", kasa: "Kasa", teknisyen: "Teknisyen", depo: "Depo (Parça)", satis: "Satış", user: "Kullanıcı" };

const emptyUser = { username: "", password: "", full_name: "", role: "teknisyen", permissions: [], active: true, commission_rate: 0 };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/users/roles/modules").then((r) => { setModules(r.data.modules); setRoleDefaults(r.data.role_defaults); });
  }, []);

  const openNew = () => { setEditing(null); setForm({ ...emptyUser, permissions: roleDefaults["teknisyen"] || [] }); setOpen(true); };
  const openEdit = (u) => { setEditing(u.id); setForm({ ...emptyUser, ...u, password: "", permissions: u.permissions || [] }); setOpen(true); };

  const submit = async () => {
    if (!form.username || (!editing && !form.password)) return toast.error("Kullanıcı adı ve şifre gerekli");
    try {
      if (editing) {
        const patch = { full_name: form.full_name, role: form.role, permissions: form.permissions, active: form.active };
        if (form.password) patch.password = form.password;
        await api.put(`/users/${editing}`, patch);
      } else {
        await api.post("/users", form);
      }
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.username} silinsin mi?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const togglePerm = (mod) => {
    if (form.permissions.includes(mod)) setForm({ ...form, permissions: form.permissions.filter((x) => x !== mod) });
    else setForm({ ...form, permissions: [...form.permissions, mod] });
  };

  const applyRoleDefaults = (role) => setForm({ ...form, role, permissions: roleDefaults[role] || [] });

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Yönetim</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Kullanıcılar</h1>
        </div>
        <Button className="bg-[#1B4D3E] hover:bg-[#163e32] text-white gap-2" onClick={openNew} data-testid="add-user-btn">
          <Plus size={16} /> Yeni Kullanıcı
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {users.map((u) => (
          <Card key={u.id} className={`border-slate-200 tp-fade ${!u.active ? "opacity-60" : ""}`} data-testid={`user-card-${u.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-11 w-11 rounded-full bg-[#1B4D3E]/10 text-[#1B4D3E] grid place-items-center font-bold">{u.full_name?.[0]?.toUpperCase() || u.username[0].toUpperCase()}</div>
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{u.full_name || u.username}</div>
                    <div className="text-xs text-slate-500 font-mono">@{u.username}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={u.role === "admin" ? "bg-[#1B4D3E] text-white" : "bg-slate-800 text-white"}>{ROLE_LABELS[u.role] || u.role}</Badge>
                  {u.commission_rate > 0 && <Badge className="bg-[#D97706] text-white text-[10px]">%{u.commission_rate} komisyon</Badge>}
                  {!u.active && <Badge variant="outline" className="text-rose-600 border-rose-300">Pasif</Badge>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 pt-1">
                {(u.permissions || []).map((p) => (
                  <Badge key={p} variant="outline" className="text-[10px]">{MODULE_LABELS[p] || p}</Badge>
                ))}
              </div>
              <div className="flex gap-1 pt-2">
                <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil size={14} /> Düzenle</Button>
                <Button size="sm" variant="outline" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield size={18} className="text-[#1B4D3E]" /> {editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Ad Soyad</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kullanıcı Adı</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} disabled={!!editing} data-testid="user-username" /></div>
              <div><Label>{editing ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Şifre"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            </div>
            <div><Label>Rol (görev tanımı)</Label>
              <Select value={form.role} onValueChange={applyRoleDefaults}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">Rolü değiştirdiğinizde varsayılan modül izinleri otomatik seçilir. Alt kısımdan özelleştirebilirsiniz.</div>
            </div>
            <div>
              <Label>Modül İzinleri</Label>
              <div className="mt-1 grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {modules.map((m) => (
                  <label key={m} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`perm-${m}`}>
                    <Checkbox checked={form.permissions.includes(m)} onCheckedChange={() => togglePerm(m)} />
                    <span>{MODULE_LABELS[m] || m}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
              <div>
                <div className="font-semibold text-slate-900">Satış Komisyonu (%)</div>
                <div className="text-xs text-slate-500">Bu kullanıcının yaptığı satışların yüzde kaçı komisyon olarak hesaplansın?</div>
              </div>
              <Input
                type="number"
                step="0.1"
                className="w-24 text-right"
                value={form.commission_rate ?? 0}
                onChange={(e) => setForm({ ...form, commission_rate: Number(e.target.value) || 0 })}
                data-testid="user-commission-rate"
              />
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
              <div>
                <div className="font-semibold text-slate-900">Aktif</div>
                <div className="text-xs text-slate-500">Pasif hesaplar giriş yapamaz.</div>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32] gap-2" onClick={submit} data-testid="save-user-btn"><KeyRound size={14} /> Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
